"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Sparkles as Sparkle, ArrowRight, Building2, Award, Users, Heart, Shield, Cpu, Crown, Sparkles } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { name: "Medicine", icon: Heart, color: "red", desc: "Clinical discipline exported to the boardroom." },
  { name: "Technology", icon: Cpu, color: "purple", desc: "Operational scale at sovereign precision." },
  { name: "Politics", icon: Crown, color: "cyan", desc: "Stakes measured in national stability." },
];

const COLOR_MAP: Record<string, string> = {
  red: "bg-red/10 text-red border-red/20 hover:bg-red/20",
  purple: "bg-purple/10 text-purple border-purple/20 hover:bg-purple/20",
  cyan: "bg-cyan/10 text-cyan border-cyan/20 hover:bg-cyan/20",
};

export function ExecutiveSummarySection() {
  return (
    <Section id="executive-summary" ariaLabel="Executive Summary" background="default">
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-14">
        <div>
          <SectionHeader
            icon={Sparkle}
            overline="The Operator Who Defies Every Category"
            overlineClassName="text-purple"
            pillClassName="glass-tinted-purple border-purple/20"
            align="left"
            title={
              <>
                Physician. Technologist. <span className="text-gradient-royal">Operator.</span>
              </>
            }
          />
          <div className="space-y-5 text-body-md text-muted-foreground leading-relaxed">
            <p>
              A physician-turned-executive whose career defies every conventional category. Holding an{" "}
              <span className="text-foreground font-semibold">MBBS with a specialization in Gynaecology</span> from
              Delhi, the career transitioned from clinical medicine into the operational leadership of a digital
              conglomerate serving the most powerful entities on the planet.
            </p>
            <p>
              As Chief Operating Officer of{" "}
              <Link href="/about/" className="text-red font-semibold hover:underline">CryptoMize and MaxiMize Infinium</Link>,
              the responsibility is the seamless execution of complex campaigns across digital, governance, and political channels,
              transformation programs, and strategic sovereignty operations.
            </p>
            <p>
              What distinguishes the approach is not the scale — but the{" "}
              <em className="text-foreground not-italic font-semibold">method</em>. The discipline forged in
              clinical practice is the same discipline applied to organizations at sovereign scale. This is
              operational excellence not as an aspiration, but as an{" "}
              <span className="text-foreground font-semibold">engineered system</span>.
            </p>
            <p>
              An outspoken advocate for{" "}
              <Link href="/the-70-percent-project/" className="text-lime font-semibold hover:underline">
                women&apos;s empowerment
              </Link>
              , entrepreneurship, excellence, and equality. The teams built are over seventy percent women — a
              statistic that emerged not from policy but from performance.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button asChild variant="outline" className="glass">
              <Link href="/about/">
                Read full biography
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <span className="text-muted-foreground opacity-30">·</span>
            <Button asChild variant="ghost" className="text-lime hover:text-lime/80">
              <Link href="/the-70-percent-project/">
                The 70% Project
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <span className="text-muted-foreground opacity-30">·</span>
            <Button asChild variant="ghost" className="text-purple hover:text-purple/80">
              <Link href="/speaking/">
                Speaking & Writing
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Three Pillars card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-strong rounded-2xl p-7 gradient-border"
        >
          <div className="text-overline text-muted-foreground mb-1">Three Pillars</div>
          <div className="text-heading-md mb-1">Medicine. Technology. Politics.</div>
          <div className="text-heading-md text-gradient-royal mb-5">One operator.</div>
          <p className="text-body-sm text-muted-foreground mb-5 leading-relaxed">
            A decade of sovereign-scale operations at the intersection of three domains.
            No other operator at this level brings a physician&apos;s discipline to sovereign execution.
          </p>
          <div className="space-y-2">
            {PILLARS.map((p) => (
              <div
                key={p.name}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${COLOR_MAP[p.color]}`}
              >
                <p.icon className="size-4 shrink-0" />
                <span className="text-body-sm font-semibold flex-1">{p.name}</span>
                <span className="text-caption text-muted-foreground">{p.desc}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}
